import ReactDOM from 'react-dom';
import React, { useMemo } from 'react';

import { usePopoverPosition } from './utils';
import { Trigger, Placement } from './types';
import * as S from './styles';

interface OverlayProps {
  children: React.ReactNode;
  target: React.ReactNode;
  eventListener?: string;
  trigger?: Trigger;
  triggers?: Trigger[];
  placement: Placement;
  style?: React.CSSProperties;
  className?: string;
  transformSize: number;
  arrowRound?: number;
  arrowSize?: string;
  contextMenu?: boolean;
}

interface ContextProps {
  positionHandle: () => void;
  hide: () => void;
  children?: React.ReactNode;
}

const initialContext: ContextProps = {
  positionHandle() {
    // do nothing.
  },
  hide() {
    // do nothing.
  },
};

export const OverLayContext: React.Context<ContextProps> =
  React.createContext(initialContext);
export const Overlay: React.FC<OverlayProps> = (props) => {
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const targetRef = React.useRef<HTMLDivElement>(null);
  const { position, placement, show, setShow, positionHandle } =
    usePopoverPosition(props, targetRef, overlayRef);

  let timeOut: NodeJS.Timeout;

  const onMouseOver = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!show) {
      setShow(true);
    } else {
      clearTimeout(timeOut);
    }
  };

  const onMouseOut = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    timeOut = setTimeout(() => {
      setShow(false);
    }, 500);
  };

  const onMouseEnter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setShow(true);
  };

  const onMouseLeave = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setShow(false);
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setShow(!show);
  };

  const { triggers, transformSize } = props;

  const getOverlayMouse = () => {
    const overlayMouse: Record<
      string,
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
    > = {};
    if (!props.contextMenu) {
      if (triggers.indexOf('hover') !== -1) {
        overlayMouse.onMouseEnter = onMouseOver;
        overlayMouse.onMouseLeave = onMouseOut;
      }
    }
    return overlayMouse;
  };

  const getTargetMouse = () => {
    const targetMouse: Record<
      string,
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
    > = {};
    if (props.contextMenu) {
      if (triggers.indexOf('click') !== -1) {
        targetMouse.onClick = onClick;
      }
    } else {
      if (triggers.indexOf('hint') !== -1) {
        targetMouse.onMouseEnter = onMouseEnter;
        targetMouse.onMouseLeave = onMouseLeave;
        targetMouse.onTouchStart = onMouseEnter;
        targetMouse.onTouchEnd = onMouseLeave;
      }
      if (triggers.indexOf('hover') !== -1) {
        targetMouse.onMouseEnter = onMouseOver;
        targetMouse.onMouseLeave = onMouseOut;
        targetMouse.onTouchStart = onMouseOver;
        targetMouse.onTouchEnd = onMouseOut;
      }
      if (triggers.indexOf('click') !== -1) {
        targetMouse.onClick = onClick;
      }
    }

    return targetMouse;
  };

  const overlayProviderValue = useMemo(
    () => ({ positionHandle, hide: () => setShow(false) }),
    [positionHandle, setShow]
  );

  return (
    <>
      <div
        style={props.style}
        className={props.className}
        ref={targetRef}
        {...getTargetMouse()}
      >
        {props.target}
      </div>
      {show &&
        ReactDOM.createPortal(
          <S.Overlay
            position={!!position}
            placement={placement}
            size={transformSize}
            arrowRound={props.arrowRound}
            arrowSize={props.arrowSize}
          >
            <OverLayContext.Provider value={overlayProviderValue}>
              <div
                className="overlay-pane"
                style={position && { top: position.top, left: position.left }}
                ref={overlayRef}
                onClick={(e) => e.stopPropagation()}
                {...getOverlayMouse()}
              >
                {props.children}
              </div>
            </OverLayContext.Provider>
          </S.Overlay>,
          document.getElementById('popover-root')!
        )}
    </>
  );
};
